import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MessageSquare, Plus } from 'lucide-react';
import { NotesDialog } from './NotesDialog';
import { useNotes } from '@/hooks/useNotes';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export const NotesWidget = () => {
  const { user } = useAuth();
  const [currentBrandId, setCurrentBrandId] = useState<string | null>(null);
  const { myNotes, sharedNotes } = useNotes(currentBrandId);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'my' | 'shared' | 'create'>('my');
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Get current brand from current_navigation table and listen to brand changes
  useEffect(() => {
    if (!user) {
      setCurrentBrandId(null);
      return;
    }

    const fetchCurrentBrand = async () => {
      try {
        const { data, error } = await supabase
          .from('current_navigation')
          .select('brand_id')
          .eq('user_id', user.id)
          .maybeSingle();
          
        if (error) {
          return;
        }
        
        if (data && data.brand_id) {
          setCurrentBrandId(data.brand_id);
        } else {
          setCurrentBrandId(null);
        }
      } catch (error) {
        // Error handled silently
      }
    };

    // Initial fetch
    fetchCurrentBrand();

    // Listen to brand change events (same as useBrandManager)
    const handleBrandChange = (event: CustomEvent) => {
      const { brandId } = event.detail;
      setCurrentBrandId(brandId);
    };

    window.addEventListener('brandChanged', handleBrandChange as EventListener);

    // Cleanup
    return () => {
      window.removeEventListener('brandChanged', handleBrandChange as EventListener);
    };
  }, [user]);


  if (!currentBrandId) return null;

  const handleOpenDialog = (type: 'my' | 'shared' | 'create') => {
    setDialogType(type);
    setSelectedNoteId(null); // Clear selected note when opening section view
    setDropdownOpen(false); // Force close dropdown first
    setTimeout(() => {
      setNotesDialogOpen(true);
    }, 100); // Small delay to ensure dropdown closes
  };

  const handleOpenSpecificNote = (note: any, type: 'my' | 'shared') => {
    setDialogType(type);
    setSelectedNoteId(note.id); // Pass the specific note ID
    setDropdownOpen(false); // Force close dropdown first
    setTimeout(() => {
      setNotesDialogOpen(true);
    }, 100); // Small delay to ensure dropdown closes
  };

  const myNotesCount = myNotes?.length || 0;
  const sharedNotesCount = sharedNotes?.length || 0;
  const totalNotes = myNotesCount + sharedNotesCount;
  const allNotes = [...myNotes, ...sharedNotes].sort((a, b) => 
    new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  );

  // Limit displayed notes to avoid very long dropdown
  const maxNotesToShow = 5;

  return (
    <>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="relative h-8 w-8 p-0"
          >
            <MessageSquare className="h-4 w-4" />
            {totalNotes > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-[10px] font-medium text-primary-foreground flex items-center justify-center">
                {totalNotes}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto bg-background/95 backdrop-blur-sm border shadow-xl z-50 p-2">
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-border/50 mb-2">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-foreground">Notes</h3>
              <div className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground">
                {totalNotes} total
              </div>
            </div>
            <button
              onClick={() => handleOpenDialog('create')}
              className="text-xs text-primary hover:text-primary/80 font-medium transition-colors"
            >
              Add Notes
            </button>
          </div>

          {/* All Notes - No separation */}
          {allNotes.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground/70 italic bg-muted/30 rounded-md mx-2">
              No notes yet
            </div>
          ) : (
            <div className="space-y-2">
              {allNotes.slice(0, maxNotesToShow).map((note) => {
                const isShared = note.is_shared;
                const noteType = myNotes.some(n => n.id === note.id) ? 'my' : 'shared';
                
                return (
                  <div key={note.id} className="mx-2">
                    <DropdownMenuItem 
                      onClick={() => handleOpenSpecificNote(note, noteType)}
                      className="flex flex-col items-start gap-2 p-3 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors cursor-pointer w-full"
                    >
                      <div className="flex items-start w-full">
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="flex items-start justify-between w-full">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className="font-medium text-sm truncate text-foreground">
                                {note.title}
                              </div>
                              {isShared && (
                                <div className="w-2 h-2 rounded-full bg-secondary flex-shrink-0"></div>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                              {new Date(note.updated_at).toLocaleDateString()}
                            </div>
                          </div>
                          {note.content && (
                            <div className="text-xs text-muted-foreground/80 truncate">
                              {(() => {
                                // Strip HTML tags and get first 10 words
                                const textContent = note.content.replace(/<[^>]*>/g, '').trim();
                                const words = textContent.split(/\s+/).slice(0, 10);
                                return words.join(' ') + (textContent.split(/\s+/).length > 10 ? '...' : '');
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Real collaboration indicator for shared notes */}
                      {isShared && note.collaborators && note.collaborators.length > 0 && (
                        <div className="flex items-center gap-2 w-full">
                          <div className="flex -space-x-1">
                            {note.collaborators.slice(0, 3).map((collaborator, index) => {
                              const initial = collaborator.full_name 
                                ? collaborator.full_name.charAt(0).toUpperCase()
                                : collaborator.email 
                                  ? collaborator.email.charAt(0).toUpperCase()
                                  : '?';
                              const colors = [
                                'from-blue-400 to-blue-600',
                                'from-green-400 to-green-600', 
                                'from-purple-400 to-purple-600',
                                'from-orange-400 to-orange-600',
                                'from-pink-400 to-pink-600'
                              ];
                              return (
                                <div 
                                  key={collaborator.user_id}
                                  className={`w-5 h-5 rounded-full bg-gradient-to-br ${colors[index % colors.length]} border border-background flex items-center justify-center text-[10px] font-medium text-white`}
                                >
                                  {initial}
                                </div>
                              );
                            })}
                            {note.collaborators.length > 3 && (
                              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 border border-background flex items-center justify-center text-[10px] font-medium text-white">
                                +{note.collaborators.length - 3}
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground/80">
                            Collaborate with {note.collaborators.slice(0, 2).map(c => {
                              const firstName = c.full_name ? c.full_name.split(' ')[0] : c.email?.split('@')[0] || 'Unknown';
                              return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
                            }).join(', ')}
                            {note.collaborators.length > 2 && ` and ${note.collaborators.length - 2} more`}
                          </span>
                        </div>
                      )}
                    </DropdownMenuItem>
                  </div>
                );
              })}
            </div>
          )}
          
          {allNotes.length > maxNotesToShow && (
            <div className="mx-2 mt-2">
              <DropdownMenuItem onClick={() => handleOpenDialog('my')} className="text-sm text-primary hover:bg-primary/5 rounded-md">
                <MessageSquare className="mr-2 h-4 w-4" />
                View all {totalNotes} notes...
              </DropdownMenuItem>
            </div>
          )}

        </DropdownMenuContent>
      </DropdownMenu>

      <NotesDialog
        open={notesDialogOpen}
        onOpenChange={setNotesDialogOpen}
        type={dialogType}
        brandId={currentBrandId}
        selectedNoteId={selectedNoteId}
      />
    </>
  );
};